import { RootState } from "../rootReducer";
import { TRemoteConfig } from "./remoteConfiguration.reducer";
import getCookie from "@utils/getCookie";

export const remoteConfigurationSelector = (state: RootState): TRemoteConfig =>
  state.remoteConfig.data || {};

export const isLoadingRemoteConfigurationSelector = (
  state: RootState
): boolean => state.remoteConfig.loading;

export const neverLoadedRemoteConfigurationSelector = (
  state: RootState
): boolean => state.remoteConfig.neverLoaded;

export const isLiveChatEnabledSelector = (state: RootState): boolean =>
  state.remoteConfig.data.FF_LIVE_CHAT;

export const usdToGbpExchangeRate = (state: RootState): number =>
  state.remoteConfig.data.USD_GBP_EXCHANGE_RATE;

export const gbpToUsdExchangeRate = (state: RootState): number =>
  state.remoteConfig.data.GBP_USD_EXCHANGE_RATE;

export const isTurnstileEnabledSelector = (state: RootState): boolean =>
  state.remoteConfig.data.FF_TURNSTILE_WEB_ENABLED ||
  getCookie("FF_TURNSTILE_WEB_ENABLED") === "true";

export const isPlaidBankAccountConnectionEnabledSelector = (
  state: RootState
): boolean =>
  state.remoteConfig.data.FF_PLAID_BANK_ACCOUNT_CONNECTION ||
  getCookie("FF_PLAID_BANK_ACCOUNT_CONNECTION") === "true";

export const isEisFundV2EnabledSelector = (state: RootState): boolean =>
  state.remoteConfig.data.FF_EIS_FUND_V2 ||
  getCookie("FF_EIS_FUND_V2") === "true";

export const isFundTenorsExplainerEnabledSelector = (
  state: RootState
): boolean =>
  state.remoteConfig.data.FF_FUND_TENORS_EXPLAINER ||
  getCookie("FF_FUND_TENORS_EXPLAINER") === "true";

export const isNewDealCardEnabledSelector = (state: RootState): boolean =>
  state.remoteConfig.data.FF_NEW_DEAL_CARD ||
  getCookie("FF_NEW_DEAL_CARD") === "true";

export const isEntityTaxResidenciesEnabledSelector = (
  state: RootState
): boolean =>
  state.remoteConfig.data.FF_ENTITY_TAX_RESIDENCIES ||
  getCookie("FF_ENTITY_TAX_RESIDENCIES") === "true";

export const isPortfolioStatementByDateEnabledSelector = (
  state: RootState
): boolean =>
  state.remoteConfig.data.FF_PORTFOLIO_STATEMENT_BY_DATE ||
  getCookie("FF_PORTFOLIO_STATEMENT_BY_DATE") === "true";

export const isEnabledDealDetailV3Selector = (state: RootState): boolean =>
  state.remoteConfig.data.FF_DEAL_DETAILS_V3 ||
  getCookie("FF_DEAL_DETAILS_V3") === "true";

export const isIsaReallocationsEnabledSelector = (state: RootState): boolean =>
  state.remoteConfig.data.FF_ISA_REALLOCATIONS ||
  getCookie("FF_ISA_REALLOCATIONS") === "true";

export const isWebHomepageEnabledSelector = (state: RootState): boolean =>
  // Controlled only by remote config and the cookie override so the feature is
  // genuinely dark everywhere (dev, E2E, staging, prod) until enabled. To view
  // it locally or on a built deploy, set the `FF_HOMEPAGE=true` cookie.
  state.remoteConfig.data.FF_HOMEPAGE || getCookie("FF_HOMEPAGE") === "true";

// Remote config names this for the app as a whole and carries its own
// per-platform conditions, so web reads FF_APP_V3. Nothing is baked into the
// build: one switch decides every environment, and the cookie overrides it in a
// single browser.
export const isWebV3IncomeFundEnabledSelector = (state: RootState): boolean =>
  state.remoteConfig.data.FF_APP_V3 ||
  getCookie("FF_WEB_V3_INCOME_FUND") === "true";

// Staging builds, including PR previews, run deal-anchored onboarding so it can be tested; a "false" cookie opts one browser out.
export const isWebOnboardingV2BEnabledSelector = (
  state: RootState
): boolean => {
  const cookie = getCookie("FF_DEAL_ANCHORED_ONBOARDING");
  if (cookie === "false") return false;

  return (
    process.env.GATSBY_ACTIVE_ENV === "staging" ||
    state.remoteConfig.data.FF_DEAL_ANCHORED_ONBOARDING ||
    cookie === "true"
  );
};

export const isUpcomingEarningsEnabledSelector = (state: RootState): boolean =>
  state.remoteConfig.data.FF_UPCOMING_EARNINGS ||
  getCookie("FF_UPCOMING_EARNINGS") === "true";

export const isDefaultBankAccountsEnabledSelector = (
  state: RootState
): boolean =>
  state.remoteConfig.data.FF_DEFAULT_BANK_ACCOUNTS ||
  getCookie("FF_DEFAULT_BANK_ACCOUNTS") === "true";
